/**
 * TimelineSortToggle Component
 *
 * Sorting strategy toggle buttons.
 * Sorts crops alphabetically or chronologically within groups.
 */

import React from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import * as Icons from '@mui/icons-material'
import { TpToggleButtonGroup } from '~/components/tp-toggle-button-group'
import { TpToggleButton } from '~/components/tp-toggle-button'
import { TimelineToggleWithPopper } from './timeline-toggle-with-popper'
import {
  useTimelineContextDispatch,
  setSortStrategy as setSortStrategyAction,
} from '../../context/timeline-context'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { CONTROLS_BREAKPOINT } from './timeline-controls-constants'
import { TpControlTooltip } from '../../../../../components/tp-control-tooltip'

/**
 * TimelineSortToggle - Sorting strategy buttons
 *
 * Options:
 * - Alphabetical (A-Z): Sort by crop name
 * - Chronological (Date): Sort by first phase start date
 */
export const TimelineSortToggle = () => {
  const theme = useTheme()
  const isExpanded = useMediaQuery(theme.breakpoints.up(CONTROLS_BREAKPOINT))
  const { sortStrategy } = useTimelineStaticContext()
  const dispatch = useTimelineContextDispatch()

  const handleChange = (e, newValue) => {
    if (newValue !== null) {
      dispatch(setSortStrategyAction({ sortStrategy: newValue }))
    }
  }

  if (isExpanded) {
    return (
      <TpControlTooltip title="Sort" offsetBottom={-8}>
        <span>
          <TimelineSortToggleContent {...{ sortStrategy, handleChange }} />
        </span>
      </TpControlTooltip>
    )
  }

  return (
    <TimelineToggleWithPopper
      title="Sort"
      icon={<Icons.Sort fontSize='small' />}
    >
      <TimelineSortToggleContent {...{ sortStrategy, handleChange }} />
    </TimelineToggleWithPopper>
  )
}

const TimelineSortToggleContent = (props) => {
  return (
    <TpToggleButtonGroup
      exclusive
      value={props.sortStrategy}
      onChange={props.handleChange}
      sx={{
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <TpToggleButton value="alphabetical">
        <Icons.SortByAlpha sx={{ fontSize: 16 }} />
      </TpToggleButton>
      <TpToggleButton value="chronological">
        <Icons.Today sx={{ fontSize: 16 }} />
      </TpToggleButton>
    </TpToggleButtonGroup>
  )
}
